
import {
  defineComponent,
  ref,
  computed,
  onBeforeUnmount,
  nextTick,
  watch,
} from "vue";
import SelectData from "@/components/Inputs/interface/IInputSelect";
import { coordenadoriasPerfilDeDados } from "@/core/listas/coordenadoriasList";

export default defineComponent({
  name: "CoordenadoriaPerfilDeDados",
  props: {
    modelValue: {
      type: [String, Number],
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    name: {
      type: String,
      default: "select",
    },
    dropdownWidth: {
      type: String,
      default: "100%",
      validator: (value: string) => {
        return /^(\d+(%|px|em|rem|vh|vw))|auto$/.test(value);
      },
    },
    redeEnsinoId: {
      type: [String, Number],
      default: null,
    },
    municipioId: {
      type: [String, Number],
      default: null,
    },
    autoFetch: {
      type: Boolean,
      default: true,
    },
    // NOVA PROP: Para indicar se é modo de edição
    isEditMode: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue", "change", "coordenadoriasLoaded"],
  setup(props, { emit }) {
    const isOpen = ref(false);
    const searchQuery = ref("");
    const dropdownRef = ref<HTMLElement | null>(null);
    const searchInput = ref<HTMLInputElement | null>(null);
    const optionsContainer = ref<HTMLElement | null>(null);
    const isTouched = ref(false);
    const isLoading = ref(false);

    const coordenadorias = ref<SelectData[]>([]);
    const visibleCount = ref(20);
    const increment = 20;

    // NOVA: Para controlar se já foi carregado inicialmente
    const initialLoadComplete = ref(false);
    // NOVA: Para armazenar o valor que deve ser selecionado após o carregamento
    const pendingValue = ref<string | number>("");

    const pegarCoordenadorias = async () => {
      try {
        if (!props.redeEnsinoId || !props.municipioId) {
          console.warn("Parâmetros insuficientes para coordenadorias");
          coordenadorias.value = [];
          return;
        }

        isLoading.value = true;
        const resultado = await coordenadoriasPerfilDeDados(
          props.redeEnsinoId,
          props.municipioId
        );

        coordenadorias.value = resultado;

        // MODIFICADO: Lógica aprimorada para seleção após carregamento
        if (props.isEditMode && (props.modelValue || pendingValue.value)) {
          // Se está em modo de edição, tenta encontrar e selecionar o valor correto
          const valueToSelect = pendingValue.value || props.modelValue;
          const option = resultado.find((opt) => opt.value == valueToSelect);

          if (option) {
            selectedValue.value = option.value;
            // Não emite change automaticamente em modo de edição para não interferir
          }
        } else if (
          !props.isEditMode &&
          resultado.length === 1 &&
          !selectedValue.value
        ) {
          // Seleção automática apenas em modo de criação se houver apenas 1 coordenadoria
          selectedValue.value = resultado[0].value;
          emit("change", { target: { value: resultado[0].value } });
        }

        initialLoadComplete.value = true;
        pendingValue.value = ""; // Limpa o valor pendente

        emit("coordenadoriasLoaded", resultado);
      } catch (error) {
        console.error("Erro ao carregar coordenadorias:", error);
        coordenadorias.value = [];
      } finally {
        isLoading.value = false;
      }
    };

    // MODIFICADO: Watcher aprimorado para tratar mudanças de parâmetros
    watch(
      [() => props.redeEnsinoId, () => props.municipioId],
      ([newRedeId, newMunicipioId], [oldRedeId, oldMunicipioId]) => {
        const isInitialLoad =
          oldRedeId === undefined && oldMunicipioId === undefined;

        if (!isInitialLoad) {
          // Só limpa a seleção se não for o carregamento inicial e se não estiver em modo de edição
          if (!props.isEditMode) {
            selectedValue.value = "";
            emit("change", { target: { value: "" } });
          }
        }

        // Armazena o valor atual para ser selecionado após o carregamento
        if (props.isEditMode && props.modelValue) {
          pendingValue.value = props.modelValue;
        }

        if (newRedeId && newMunicipioId) {
          pegarCoordenadorias();
        } else {
          coordenadorias.value = [];
          initialLoadComplete.value = false;
        }
      },
      { immediate: true }
    );

    // NOVO: Watcher para reagir a mudanças no modelValue após o carregamento
    watch(
      () => props.modelValue,
      (newValue) => {
        if (initialLoadComplete.value && coordenadorias.value.length > 0) {
          // Se já carregou as coordenadorias, atualiza a seleção
          const option = coordenadorias.value.find(
            (opt) => opt.value == newValue
          );
          if (option || !newValue) {
            selectedValue.value = newValue;
          }
        } else if (!initialLoadComplete.value && newValue) {
          // Se ainda não carregou, armazena para seleção posterior
          pendingValue.value = newValue;
        }
      }
    );

    watch(searchQuery, () => {
      visibleCount.value = 20;
    });

    const selectedValue = computed({
      get: () => props.modelValue,
      set: (newValue: string | number) => {
        emit("update:modelValue", newValue);
      },
    });

    const options = computed(() => {
      return coordenadorias.value;
    });

    const selectedLabel = computed(() => {
      if (!Array.isArray(options.value)) return "";
      if (selectedValue.value == undefined) return "";
      const selectedOption = options.value.find(
        (option) => option.value === props.modelValue
      );
      return selectedOption ? selectedOption.label : "";
    });

    const filteredOptions = computed(() => {
      return options.value.filter(({ label }) =>
        label?.toLowerCase().includes(searchQuery.value.toLowerCase())
      );
    });

    const displayedOptions = computed(() => {
      let optionsList = props.required
        ? [...filteredOptions.value]
        : [{ value: "", label: " " }, ...filteredOptions.value];

      return optionsList.slice(0, visibleCount.value);
    });

    const borderStyle = computed(() => {
      if (!isTouched.value) return "";
      if (props.required && !selectedValue.value) {
        return "1px solid red";
      } else if (!props.required) {
        return "1px solid green";
      }
      return "";
    });

    const toggleDropdown = () => {
      isOpen.value = !isOpen.value;
      if (isOpen.value) {
        searchQuery.value = "";
        visibleCount.value = 20;
        nextTick(() => {
          searchInput.value?.focus();
        });
      }
    };

    const selectOption = (value: any, label: any) => {
      selectedValue.value = value;
      emit("change", { target: { value } });
      isOpen.value = false;
    };

    const handleClickOutside = (event: Event) => {
      if (
        dropdownRef.value &&
        !dropdownRef.value.contains(event.target as Node)
      ) {
        isOpen.value = false;
      }
    };

    const selectFirstOption = () => {
      if (filteredOptions.value.length > 0) {
        const { value, label } = filteredOptions.value[0];
        selectOption(value, label);
      }
    };

    const onScroll = () => {
      const container = optionsContainer.value;
      if (container) {
        const { scrollTop, clientHeight, scrollHeight } = container;
        if (scrollTop + clientHeight >= scrollHeight - 20) {
          if (visibleCount.value < filteredOptions.value.length) {
            visibleCount.value = Math.min(
              visibleCount.value + increment,
              filteredOptions.value.length
            );
          }
        }
      }
    };

    document.addEventListener("click", handleClickOutside);
    onBeforeUnmount(() => {
      document.removeEventListener("click", handleClickOutside);
    });

    return {
      isOpen,
      searchQuery,
      filteredOptions,
      displayedOptions,
      toggleDropdown,
      selectOption,
      selectedLabel,
      dropdownRef,
      searchInput,
      optionsContainer,
      selectFirstOption,
      borderStyle,
      onScroll,
      isLoading,
      pegarCoordenadorias,
    };
  },
});
